import { createContext, useState, useCallback, useMemo, useContext } from 'react';
import { jsx } from 'react/jsx-runtime';

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}
function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}

var _excluded = ["$fallback", "$prefix", "$plural"];
/**
 * @deprecated Consult with frontend chapter for better alternatives
 */
var TranslationContext = /*#__PURE__*/createContext(undefined);
var PLURAL_SUFFIX = '_plural';

/**
 * @deprecated Consult with frontend chapter for better alternatives
 */
var makePlaceholderRegExp = function makePlaceholderRegExp(search) {
  return new RegExp("\\{\\{" + search + "\\}\\}", 'g');
};

/**
 * @deprecated Consult with frontend chapter for better alternatives
 */
var isNumberAndNotOne = function isNumberAndNotOne(_ref) {
  _ref[0];
    var value = _ref[1];
  return typeof value === 'number' && value !== 1;
};

/**
 * @deprecated Consult with frontend chapter for better alternatives
 */
var replaceWithValues = function replaceWithValues(carry, _ref2) {
  var key = _ref2[0],
    value = _ref2[1];
  var regExp = makePlaceholderRegExp(key);
  return carry.replace(regExp, value);
};

/**
 * @deprecated Consult with frontend chapter for better alternatives
 */
var warnAboutMissingKey = function warnAboutMissingKey(key) {
  if (typeof window !== 'undefined') {
    console.warn("unknown key: \"" + key + "\""); // eslint-disable-line no-console
  }

  return key;
};

/**
 * @deprecated Consult with frontend chapter for better alternatives
 */

/**
 * @deprecated Consult with frontend chapter for better alternatives
 */
function TranslationContextProvider(_ref3) {
  var children = _ref3.children,
    _ref3$initialState = _ref3.initialState,
    initialState = _ref3$initialState === void 0 ? {} : _ref3$initialState;
  var _useState = useState(initialState),
    translation = _useState[0],
    setTranslation = _useState[1];
  var addTranslation = useCallback(function (newTranslation) {
    if (newTranslation) {
      setTranslation(function (existingTranslation) {
        return _extends({}, existingTranslation, newTranslation);
      });
    }
  }, []);

  /**
   * @param {string} key
   * @param {{$fallback?: string; $prefix?: string; $plural?: boolean;}} values
   *
   * @returns {string}
   */
  var translate = useCallback(function (key, _temp) {
    var _ref4 = _temp === void 0 ? {} : _temp,
      _ref4$$fallback = _ref4.$fallback,
      $fallback = _ref4$$fallback === void 0 ? key : _ref4$$fallback,
      $prefix = _ref4.$prefix,
      _ref4$$plural = _ref4.$plural,
      $plural = _ref4$$plural === void 0 ? false : _ref4$$plural,
      values = _objectWithoutPropertiesLoose(_ref4, _excluded);
    var fullKey = $prefix ? $prefix + "." + key : key;
    var fallbackKey = $prefix ? $prefix + "." + $fallback : $fallback;

    // future optimization might include checking Object.values.length === 0 here,
    // moving entries after the early return, as Object.entries is ~ 33% slower than .values
    var entries = Object.entries(values);

    // no values passed, thus nothing to replace
    if (entries.length === 0) {
      var _data = translation[fullKey] || translation[fallbackKey];
      return _data || warnAboutMissingKey(fullKey || fallbackKey);
    }

    // a string is considered plural as soon as one of the values is a number and not 1
    var requiresPlural = $plural ? entries.some(isNumberAndNotOne) : false;
    var finalKey = requiresPlural ? fullKey + PLURAL_SUFFIX : fullKey;
    var finalFallbackKey = requiresPlural ? fallbackKey + PLURAL_SUFFIX : fallbackKey;
    var data = translation[finalKey] || translation[finalFallbackKey];
    return data ? entries.reduce(replaceWithValues, data) : warnAboutMissingKey(finalFallbackKey || finalKey);
  }, [translation]);
  return /*#__PURE__*/jsx(TranslationContext.Provider, {
    value: useMemo(function () {
      return {
        addTranslation: addTranslation,
        translate: translate
      };
    }, [addTranslation, translate]),
    children: children
  });
}

/**
 * @deprecated Consult with frontend chapter for better alternatives
 */
function useTranslationContext() {
  return useContext(TranslationContext);
}

/**
 * @deprecated Consult with frontend chapter for better alternatives
 */
function makeTranslation(moduleName) {
  if (moduleName === void 0) {
    moduleName = '';
  }
  return function useTranslation(componentName) {
    if (componentName === void 0) {
      componentName = '';
    }
    var _useTranslationContex = useTranslationContext(),
      translate = _useTranslationContex.translate;
    return function t(key, values) {
      var _values;
      if (values === void 0) {
        values = {};
      }
      // call translate without any prefix
      if ((_values = values) != null && _values.$prefix || !moduleName && !componentName) {
        return translate(key, values);
      }

      // call translate with prefix
      if (moduleName && componentName) {
        return translate(key, _extends({}, values, {
          $prefix: moduleName + "_" + componentName
        }));
      }

      // call translate with moduleName prefix
      return translate(key, _extends({}, values, {
        $prefix: moduleName
      }));
    };
  };
}

function withTranslationContext(Component, moduleName, componentName) {
  var displayName = "withTranslationContext(" + (Component.displayName || Component.name || 'Unknown') + ")";
  var useTranslation = makeTranslation(moduleName);
  var C = function C(props) {
    var t = useTranslation(componentName);
    return /*#__PURE__*/jsx(TranslationContext.Consumer, {
      children: function children(_ref) {
        var addTranslation = _ref.addTranslation;
        return /*#__PURE__*/jsx(Component, _extends({}, props, {
          addTranslation: addTranslation,
          t: t
        }));
      }
    });
  };
  C.displayName = displayName;
  C.WrappedComponent = Component;
  return C;
}

export { TranslationContext, TranslationContextProvider, isNumberAndNotOne, makePlaceholderRegExp, makeTranslation, replaceWithValues, useTranslationContext, warnAboutMissingKey, withTranslationContext };
//# sourceMappingURL=index.js.map
